import { ErrorMessage, Field, Form, Formik } from "formik";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import SimpleModal from "cpl-react-common/components/Modals/SimpleModal";
import { createNewClientAddress, selectHasDifferentBillingAddressSet, selectNewAddress } from "./ClientAddressesSlice";
import { selectClientID } from "../ContactDetailsSlice";
import { postcodeValidationRegex } from "cpl-react-common/components/Constants";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import { useState } from "react";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { NewClientAddressModel } from "services/api/cplapi/CPLAPI";

type NewAddressModalProps = {
  ShowNewAddressModal: boolean;
  DismissNewAddressModalCallback(): void;
};

const getValidationSchema = () =>
  Yup.object().shape({
    address: Yup.string().required("Address is required"),
    postCode: Yup.string()
      .required("Postcode is required")
      .matches(postcodeValidationRegex, "Postcode not valid format"),
  });

const NewClientAddressModal: React.FC<NewAddressModalProps> = ({
  ShowNewAddressModal,
  DismissNewAddressModalCallback,
}) => {
  const thunkDispatch = useThunkDispatch();
  const newAddressValues = useSelector(selectNewAddress);
  const clientAccountNumber = useSelector(selectClientID);
  const hasBillingAddressSet = useSelector(selectHasDifferentBillingAddressSet(""));
  const [showWarning, setShowWarning] = useState(false);
  const [pendingAddress, setPendingAddress] = useState<NewClientAddressModel | null>(null);

  const handleCloseAlert = (answer: boolean) => {
    setShowWarning(false);
    if (pendingAddress) {
      pendingAddress.isBilling = answer;
      thunkDispatch(createNewClientAddress({ newClientAddress: pendingAddress }));
      setPendingAddress(null);
      DismissNewAddressModalCallback();
    }
  };

  function onSaveClick(newClientAddress: NewClientAddressModel) {
    if (hasBillingAddressSet && newClientAddress.isBilling) {
      setPendingAddress(newClientAddress);
      setShowWarning(true);
      return;
    }
    thunkDispatch(createNewClientAddress({ newClientAddress }));
    DismissNewAddressModalCallback();
  }

  return (
    <>
      <YesNoAlert
        showAlert={showWarning}
        alertTitle="Billing Address"
        alertText="A billing address is already set. Would you like to set this as the billing address?"
        style={Style.Info}
        callBack={handleCloseAlert}
      />
      <SimpleModal
        modalTitle="New Contact Address"
        showModal={ShowNewAddressModal}
        dismissModalCallback={() => {
          DismissNewAddressModalCallback();
        }}
      >
        <div className="card card-body">
          <Formik
            initialValues={newAddressValues}
            validationSchema={getValidationSchema()}
            onSubmit={(values) => {
              onSaveClick(values);
            }}
            enableReinitialize
          >
            <Form>
              <Field name="clientAccountNumber" type="hidden" value={clientAccountNumber}></Field>
              <div className="row">
                <div className="col">
                  <div className="row">
                    <div className="col-3">
                      <label htmlFor="newAddress" className="col-form-label fw-bold required">
                        Full Address
                      </label>
                    </div>
                    <div className="col-9">
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          <ErrorMessage name="address" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Field
                        data-test-id="new-address-modal__full-address-textarea"
                        name="address"
                        component="textarea"
                        placeholder="890 Bridge Road
                            Edinburgh
                            Midlothian"
                        aria-placeholder="890 Bridge Road
                            Edinburgh
                            Midlothian"
                        className="form-control form-control-sm form-control-solid"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-4">
                  <label htmlFor="newPostCode" className="col-form-label fw-bold required">
                    Postcode
                  </label>
                  <Field
                    data-test-id="new-address-modal__postcode-input"
                    name="postCode"
                    placeholder="EH1 1AA"
                    aria-placeholder="EH1 1AA"
                    className="form-control form-control-sm form-control-solid"
                  />
                </div>
                <div className="col-8">
                  <label htmlFor="newDescription" className="col-form-label fw-bold">
                    Description
                  </label>
                  <Field
                    data-test-id="new-address-modal__description-input"
                    name="description"
                    placeholder="Billing office"
                    aria-placeholder="Billing office"
                    className="form-control form-control-sm form-control-solid"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <ErrorMessage name="postCode" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-8">
                  <div className="row mt-1">
                    <div className="col align-self-center">
                      <Field
                        data-test-id="new-address-modal__billing-address-checkbox"
                        name="isBilling"
                        type="checkbox"
                        className="form-check-input"
                      />
                      <label htmlFor="isNewBillingAddress" className="form-check-label ms-2 fw-bold">
                        Is billing address?
                      </label>
                    </div>
                  </div>
                  <div className="row mt-1">
                    <div className="col align-self-center">
                      <Field
                        data-test-id="new-address-modal__internal-billing-address-checkbox"
                        name="isInternalBilling"
                        type="checkbox"
                        className="form-check-input"
                      />
                      <label htmlFor="isNewInternalBillingAddress" className="form-check-label ms-2 fw-bold">
                        Is internal billing address?
                      </label>
                    </div>
                  </div>
                  <div className="row mt-1">
                    <div className="col align-self-center">
                      <Field
                        data-test-id="new-address-modal__forwarding-address-checkbox"
                        name="isForwarding"
                        type="checkbox"
                        className="form-check-input"
                      />
                      <label htmlFor="isNewForwardingAddress" className="form-check-label ms-2 fw-bold">
                        Is forwarding address?
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-4 align-self-end">
                  <button
                    data-test-id="new-address-modal__save-button"
                    aria-label="Save"
                    type="submit"
                    className="cpl-btn cpl-btn-success m-1"
                  >
                    Save
                  </button>
                  <button
                    data-test-id="new-address-modal__cancel-button"
                    aria-label="Cancel"
                    type="button"
                    className="cpl-btn cpl-btn-secondary m-1"
                    onClick={() => DismissNewAddressModalCallback()}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div className="d-flex justify-content-end"></div>
            </Form>
          </Formik>
        </div>
      </SimpleModal>
    </>
  );
};

export default NewClientAddressModal;
